// Corps de la fiche tour, partage par le rendu serveur (page.tsx, tours publics) et par
// la vue client d'un tour reserve a un client (PrivateTourView, GT-261007-HVGQ), pour qu'un
// tour reserve s'affiche exactement comme les autres pour ceux qui peuvent le reserver.
//
// Pas de "use client" ni d'API serveur ici : rendu en composant serveur depuis page.tsx,
// en composant client depuis PrivateTourView. `t` est fourni par l'appelant (getServerT
// cote serveur, useTranslation cote client).

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Route } from "next";
import TourFavouriteButton from "./TourFavouriteButton";
import TourLeftBody from "./TourLeftBody";
import TourSidebar from "./TourSidebar";
import AccountDiscountNotice from "@/components/AccountDiscountNotice";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface TourPageViewProps {
  lang: string;
  currency: string;
  tour: any;
  city: any;
  galleries: any[];
  continents: any[];
  t: Translate;
}

// Server-safe title-case helper — same logic as the client titleCase in
// CityHero so server and client breadcrumb labels render identically.
const titleCase = (s?: string): string => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-")
    )
    .join(" ");
};

// HTML-aware first-N-words truncator (mirrors the client utility — pure
// string ops, safe to run on the server). Keeps any tags intact so the
// CMS-styled paragraphs don't lose their formatting.
const VOID_TAGS = new Set([
  "br","hr","img","input","meta","link","area","base","col","embed","source","track","wbr",
]);
const extractFirstWords = (html: string, wordLimit = 100): string => {
  if (!html) return "";
  const tokens = html.split(/(<[^>]+>)/);
  const openStack: string[] = [];
  let wordCount = 0;
  let truncated = false;
  const out: string[] = [];
  for (const tok of tokens) {
    if (truncated) break;
    if (!tok) continue;
    if (tok.startsWith("<")) {
      out.push(tok);
      const closeMatch = tok.match(/^<\/([a-zA-Z0-9]+)/);
      if (closeMatch) {
        const tag = closeMatch[1].toLowerCase();
        for (let i = openStack.length - 1; i >= 0; i--) {
          if (openStack[i] === tag) {
            openStack.splice(i, 1);
            break;
          }
        }
      } else if (!tok.endsWith("/>")) {
        const openMatch = tok.match(/^<([a-zA-Z0-9]+)/);
        if (openMatch) {
          const tag = openMatch[1].toLowerCase();
          if (!VOID_TAGS.has(tag)) openStack.push(tag);
        }
      }
    } else {
      const parts = tok.split(/(\s+)/);
      let acc = "";
      for (const part of parts) {
        if (/\S/.test(part)) {
          if (wordCount >= wordLimit) {
            truncated = true;
            break;
          }
          wordCount++;
        }
        acc += part;
      }
      out.push(acc + (truncated ? "…" : ""));
    }
  }
  while (openStack.length) out.push(`</${openStack.pop()}>`);
  return out.join("");
};

const formatDurationValue = (value: number | null) =>
  value === null ? "" : Number.isInteger(value) ? `${value}` : value.toFixed(1);

export default function TourPageView({ lang, currency, tour, city, galleries, continents, t }: TourPageViewProps) {
  // ---- Derived values for the SSR shell -----------------------------
  const tourTitleRaw: string = tour?.entry?.data?.[0]?.title || "";
  const tourTitlePlain =
    (typeof tourTitleRaw === "string"
      ? tourTitleRaw.replace(/<[^>]*>/g, "")
      : "") ||
    tour?.city?.name ||
    "Tour";
  const tourDescription: string = tour?.entry?.data?.[0]?.content || "";

  const availableLanguages = tour?.tourLanguages || [];
  const categories = (tour?.tourByCategories || []).filter(
    (c: any) => c?.activated === true
  );

  // Duration range pill ("2 à 8 H").
  const durations = tour?.durations || [];
  const activatedDurationValues = durations
    .filter((d: any) => d?.activated)
    .map((d: any) => Number(d?.duration))
    .filter((v: number) => Number.isFinite(v) && v > 0)
    .sort((a: number, b: number) => a - b);
  const hasDurations = activatedDurationValues.length > 0;
  const minDuration = hasDurations ? activatedDurationValues[0] : null;
  const maxDuration = hasDurations
    ? activatedDurationValues[activatedDurationValues.length - 1]
    : null;
  const durationLabel = hasDurations
    ? minDuration === maxDuration
      ? t("tour_duration_single", { value: formatDurationValue(minDuration) })
      : t("tour_duration_range", {
          min: formatDurationValue(minDuration),
          max: formatDurationValue(maxDuration),
        })
    : "";

  // Breadcrumb chain — server resolves city, country and continent labels
  // from the CMS-localised titles. Falls back to nameTranslated → name →
  // empty so we never ship a slug to the user.
  const cityName = titleCase(
    (city as any)?.entry?.data?.[0]?.title ||
      (tour as any)?.city?.entry?.data?.[0]?.title ||
      (city as any)?.nameTranslated ||
      (tour as any)?.city?.nameTranslated ||
      city?.name ||
      tour?.city?.name ||
      ""
  );
  const citySlugResolved = tour?.city?.slug || city?.slug || "";
  const countryInfo: any =
    (city as any)?.countryInfo || (tour as any)?.city?.countryInfo;
  const countryName = titleCase(
    countryInfo?.entry?.data?.[0]?.title ||
      countryInfo?.nameTranslated ||
      countryInfo?.name ||
      ""
  );
  const countrySlug = countryInfo?.slug || "";
  const continentCode = (countryInfo?.continentId || "").toUpperCase();
  const continent = continentCode
    ? continents.find((c: any) => (c.code || "").toUpperCase() === continentCode)
    : null;
  const continentName = titleCase(
    continent?.entry?.data?.[0]?.title ||
      continent?.nameTranslated ||
      continent?.name ||
      ""
  );
  const continentSlug =
    continent?.slug || (continent?.code || "").toLowerCase();

  const heroGallery = (galleries || []).slice(0, 5);
  const heroImage = heroGallery[0]?.path;

  // Server-rendered initial description body. The client island gets
  // both the truncated form (matches SSR) and the full text so it can
  // swap on "see more" without a fresh fetch.
  const initialDescriptionMobile = extractFirstWords(tourDescription, 60);
  const initialDescriptionDesktop = extractFirstWords(tourDescription, 150);

  const photoCount = galleries?.length || 0;

  return (
    <div className="gdx nc-Tour" style={{ color: "var(--g-ink-900)" }}>
      {/* ----------- MOBILE breadcrumb (Home > City > Tour) ---------- */}
      <nav
        aria-label={t("breadcrumb", { defaultValue: "Fil d'Ariane" })}
        className="flex md:hidden items-center gap-1.5 text-[12px] mb-3 px-1 overflow-hidden whitespace-nowrap"
        style={{ color: "var(--g-ink-500)" }}
      >
        <Link
          href={`/${lang}/${currency}` as Route}
          className="hover:underline shrink-0"
        >
          {t("home", { defaultValue: "Accueil" })}
        </Link>
        {cityName && citySlugResolved && (
          <>
            <span aria-hidden="true">›</span>
            <Link
              href={`/${lang}/${currency}/city/${citySlugResolved}` as Route}
              className="hover:underline shrink-0"
            >
              {cityName}
            </Link>
          </>
        )}
        <span aria-hidden="true">›</span>
        <span
          style={{ color: "var(--g-burgundy)", fontWeight: 700 }}
          className="truncate min-w-0"
          aria-current="page"
        >
          {tourTitlePlain}
        </span>
      </nav>

      {/* ----------- DESKTOP breadcrumb (full chain) ----------------- */}
      <nav
        aria-label={t("breadcrumb", { defaultValue: "Fil d'Ariane" })}
        className="hidden md:flex flex-wrap items-center gap-2 text-[13px] mb-4"
        style={{ color: "var(--g-ink-500)" }}
      >
        <Link
          href={`/${lang}/${currency}` as Route}
          className="hover:underline"
        >
          {t("home", { defaultValue: "Accueil" })}
        </Link>
        <span aria-hidden="true">›</span>
        <Link
          href={`/${lang}/${currency}/continents` as Route}
          className="hover:underline"
        >
          {t("breadcrumb_continents", { defaultValue: "Continents" })}
        </Link>
        {continentName && (
          <>
            <span aria-hidden="true">›</span>
            <Link
              href={
                `/${lang}/${currency}/continent/${continentSlug}` as Route
              }
              className="hover:underline"
            >
              {continentName}
            </Link>
          </>
        )}
        {countryName && countrySlug && (
          <>
            <span aria-hidden="true">›</span>
            <Link
              href={`/${lang}/${currency}/country/${countrySlug}` as Route}
              className="hover:underline"
            >
              {countryName}
            </Link>
          </>
        )}
        {cityName && citySlugResolved && (
          <>
            <span aria-hidden="true">›</span>
            <Link
              href={`/${lang}/${currency}/city/${citySlugResolved}` as Route}
              className="hover:underline"
            >
              {cityName}
            </Link>
          </>
        )}
        <span aria-hidden="true">›</span>
        <span
          style={{ color: "var(--g-burgundy)", fontWeight: 700 }}
          aria-current="page"
        >
          {tourTitlePlain}
        </span>
      </nav>

      {/* ------------------------- HERO ------------------------------ */}
      <header className="mb-6 lg:mb-8">
        <div
          className="relative grid gap-1 md:gap-1.5 overflow-hidden grid-cols-1 md:grid-cols-[2fr_1fr_1fr] auto-rows-[280px] md:auto-rows-[230px]"
          style={{ borderRadius: 18 }}
        >
          {heroImage && (
            <Link
              href={`?modal=PHOTO_TOUR_SCROLLABLE` as Route}
              scroll={false}
              className="group relative cursor-zoom-in overflow-hidden md:row-span-2"
              aria-label={t("show_all_photos", {
                defaultValue: "Voir toutes les photos",
              })}
            >
              <Image
                fill
                priority
                fetchPriority="high"
                quality={70}
                className="object-cover transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                src={heroImage}
                alt={tourTitlePlain}
                sizes="(max-width: 768px) 100vw, 66vw"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"
              />
              <span
                className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[13px] font-semibold pointer-events-none"
                style={{
                  background: "rgba(255,255,255,.95)",
                  color: "var(--g-ink-900)",
                  borderRadius: 22,
                  padding: "8px 14px",
                  boxShadow: "0 2px 8px rgba(15,26,20,.10)",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                {t("show_all_photos_count", {
                  defaultValue: "Voir les {{count}} photos",
                  count: photoCount,
                })}
              </span>

            </Link>
          )}

          {/* Favori — mobile et tablette, posé sur la photo (îlot client : la page est
              rendue sans utilisateur). Sur ordinateur, il est au-dessus du formulaire de
              réservation (TourSidebar). */}
          {tour?.uuid && (
            <div className="absolute top-3 right-3 z-10 lg:hidden">
              <TourFavouriteButton tourUuid={tour.uuid} variant="overlay" />
            </div>
          )}

          {/* Desktop thumbnails — hidden on mobile. */}
          {heroGallery.slice(1, 5).map((gallery, index) => (
            <Link
              key={index}
              href={`?modal=PHOTO_TOUR_SCROLLABLE` as Route}
              scroll={false}
              className="group relative cursor-zoom-in overflow-hidden hidden md:block"
              aria-label={t("show_all_photos", {
                defaultValue: "Voir toutes les photos",
              })}
            >
              <Image
                fill
                loading="lazy"
                decoding="async"
                quality={65}
                className="object-cover transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                src={gallery.path}
                alt=""
                sizes="22vw"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"
              />
            </Link>
          ))}
        </div>
      </header>

      {/* ------------------------- MAIN ------------------------------ */}
      <main className="relative z-10 flex flex-col gap-10 lg:gap-14">
        <div className="grid gap-8 lg:gap-12 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]">
          {/* LEFT — pills + title + (client) ratings/description/tabs */}
          <div className="min-w-0">
            {/* Pills row — server-rendered, fully static */}
            <div className="flex flex-wrap gap-2 mb-3">
              {categories.map((c: any) => (
                <Link
                  key={
                    c.tourCategory?.uuid || c.tourCategory?.nameTranslated
                  }
                  href={
                    c.tourCategory?.slug
                      ? (`/tour-category/${c.tourCategory.slug}` as Route)
                      : ("#" as Route)
                  }
                  className="gdx-pill green capitalize"
                >
                  {c.tourCategory?.nameTranslated}
                </Link>
              ))}
              {hasDurations && (
                <span className="gdx-pill cream">{durationLabel}</span>
              )}
              {tour?.isPrivateOffer && (
                <span className="gdx-pill outline">
                  {t("private_offer_badge", { defaultValue: "Offre personnalisée · réservée à votre compte" })}
                </span>
              )}
              {availableLanguages.length > 0 && (
                <span
                  className="inline-flex items-center ml-auto"
                  style={{
                    color: "var(--g-burgundy)",
                    fontSize: 13,
                    fontWeight: 600,
                    gap: 8,
                    padding: "6px 0",
                  }}
                >
                  <span className="hidden md:inline">
                    {t("available_languages", {
                      defaultValue: "Langues disponibles",
                    })}{" "}
                    :
                  </span>
                  <span
                    className="inline-flex items-center"
                    style={{ gap: 4 }}
                  >
                    {availableLanguages
                      .map((l: any) =>
                        l.language?.split("_")[1]?.toLowerCase()
                      )
                      .filter(Boolean)
                      .map((cc: string) => (
                        <img
                          key={cc}
                          src={`/images/flags/4x3/${cc}.svg`}
                          alt={cc.toUpperCase()}
                          width={20}
                          height={14}
                          style={{
                            borderRadius: 2,
                            display: "inline-block",
                            verticalAlign: "middle",
                          }}
                        />
                      ))}
                  </span>
                </span>
              )}
            </div>

            {/* Title — SSR'd so it lands in the initial HTML alongside
                the hero, giving the LCP candidate a clean text neighbour
                that can paint instantly. */}
            {tourTitleRaw ? (
              <h1
                className="font-extrabold leading-tight mb-3"
                style={{
                  color: "var(--g-burgundy)",
                  fontSize: "clamp(26px, 4vw, 38px)",
                }}
                dangerouslySetInnerHTML={{ __html: tourTitleRaw }}
              />
            ) : (
              <h1
                className="font-extrabold leading-tight mb-3"
                style={{
                  color: "var(--g-burgundy)",
                  fontSize: "clamp(26px, 4vw, 38px)",
                }}
              >
                {tourTitlePlain}
              </h1>
            )}

            {/* Remise du compte pro (GT-261007-HVGQ) : ilot client, rien au rendu serveur.
                Sur ordinateur elle figure deja dans le formulaire de reservation. */}
            <div className="lg:hidden">
              <AccountDiscountNotice cityId={tour?.city?.id ?? null} />
            </div>

            {/* Client island for everything below the title */}
            <TourLeftBody
              initialDescription={initialDescriptionMobile}
              fullDescription={tourDescription}
              fullDescriptionDesktop={initialDescriptionDesktop}
            />
          </div>

          {/* RIGHT — desktop booking sidebar (client island) */}
          <TourSidebar tourUuid={tour?.uuid} />
        </div>
      </main>
    </div>
  );
}
